import React, { useEffect, useState, useRef } from "react";
import { Navigate } from "@tanstack/react-router";
import { AuthUser, getCurrentUser, signOut } from "@aws-amplify/auth";
import { Shield } from "lucide-react";
import toast from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";
import { clearAllAuthStorage, isRevokedRefreshTokenError } from "../utils/authStorage";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { refreshUserDetails, userDetails } = useAuth();
  const hasFetchedRef = useRef(false);

  // Run once per mount: get Cognito user then fetch /me. Prevents /me from being called again and again.
  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const fetchUser = async () => {
      try {
        const currentUser = await getCurrentUser();
        setUser(currentUser);

        // Fetch user details from API (no PII from storage)
        try {
          await refreshUserDetails();
        } catch (error) {
          // ignore
        }
      } catch (error) {
        if (isRevokedRefreshTokenError(error)) {
          try {
            await signOut();
          } catch {
            // ignore
          }
          clearAllAuthStorage();
          toast.error("Your session has expired. Please sign in again.");
          window.location.href = "/login";
          return;
        }
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUser();
  }, [refreshUserDetails]);

  // If userDetails was cleared (e.g. backend returned 404 and we signed out), re-verify Cognito session
  useEffect(() => {
    if (!userDetails && user && !isLoading) {
      getCurrentUser()
        .then((currentUser) => {
          if (!currentUser) setUser(null);
        })
        .catch(async (error) => {
          if (isRevokedRefreshTokenError(error)) {
            try {
              await signOut();
            } catch {
              // ignore
            }
            clearAllAuthStorage();
            toast.error("Your session has expired. Please sign in again.");
            window.location.href = "/login";
            return;
          }
          setUser(null);
        });
    }
  }, [userDetails, user, isLoading]);

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/60 from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center z-50">
        <div className="flex flex-col items-center justify-center">
          <div className="relative">
            {/* Outer spinning ring */}
            <div className="w-24 h-24 border-4 border-[#e761a0] border-t-[#e761a0] rounded-full animate-spin"></div>
            {/* Inner icon */}
            <Shield className="w-8 h-8 text-[#e761a0] absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="text-[#e761a0] text-lg mt-6 font-bold">Loading...</p>
          <p className="text-white text-sm mt-2">
            Please wait while we verify your session
          </p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};
